import { useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import moment from "moment-timezone";
import { Paginator } from "primereact/paginator";
import { parse, stringify } from "query-string";
import { useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  from,
  interval,
  map,
  mapTo,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";

import { Authorization } from "../../../common/AuthorizationCpt";
import { HTTP_METHODS } from "../../../utils/constants";
import { dateToRegularFormat } from "../../../utils/dates";
import { networkBackendRequest } from "../../../utils/networkUtils";
import { formatMoney } from "../../../utils/numbers";
import { CurrencyContext } from "../../CurrencyContext/CurrencyContext";
import { ExportButton } from "../../IconButtons";
import {
  ColoredTag,
  InvoiceContainer,
  VolanteId,
} from "../../Invoice/InvoiceStyles";

import { InvoicesContainer, TableHeader } from "../../Invoices/InvoicesStyles";
import PaymentLoader from "../../PaymentLoader/PaymentLoader";
import StandaloneMessage from "../../StandaloneMessage/StandaloneMessage";
import { getPaymentTableHeaders, ITEMS_PER_PAGE } from "../common/utils";
import { paymentConvertResponse, POLLING_INTERVAL } from "../common";

import { Container } from "../PaymentAdminStyles";
import { PaymentPaidAdminParams$ } from "../interfaces/PaidBackoffice.interface";

export const PaymentPaidAdminTable = (props: PaymentPaidAdminParams$) => {
  const { params$ } = props;

  const destroy$Ref = useRef(new Subject());
  const destroy$: any = destroy$Ref.current;

  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);

  const loadInvoices = useAsyncExecutor2(
    (params) => {
      const destroy$ = destroy$Ref.current;
      return interval(POLLING_INTERVAL).pipe(
        takeUntil(destroy$),
        startWith(params),
        mapTo(params),
        switchMap((params) => {
          const request = networkBackendRequest(
            `/quotations/paid/backoffice?${stringify(params)}`,
            HTTP_METHODS.GET,
            undefined,
            undefined
          )
            .then((response) => response.json())
            .then(paymentConvertResponse);

          return from(request);
        })
      );
    },
    {
      params$: params$.pipe(
        takeUntil(destroy$Ref.current),
        map((params) => {
          const paramsValid = {
            company_info: params.company_info,
            from_date: format(params.from_date, "yyyy-MM-dd"),
            to_date: format(params.to_date, "yyyy-MM-dd"),
            order: "descending",
            page: params.page ?? 1,
            page_size: ITEMS_PER_PAGE,
            payment_status_list: "paid",
            volante_nums: params.volante_nums,
            id_pedidos: params.id_pedidos,
            tci_nums: params.tci_nums,
          };
          if (params.num.length === 6) {
            return {
              ...paramsValid,
              dam_num: `235-${params.year}-${params.regimen}-${params.num}`,
            };
          }
          return {
            ...paramsValid,
          };
        })
      ),
    }
  );

  const [useDollars] = useContext(CurrencyContext);
  const titles = getPaymentTableHeaders(useDollars);

  const history = useHistory();

  const gridTemplateColumns = "1.5fr 1fr 2fr 1fr 1fr 1fr 15rem 12rem";

  const handleDownload = (objectName: string) => {
    generateDownloadUrl(objectName).then((url) => {
      window.open(url, "_blank");
    });
  };

  return (
    <>
      <InvoicesContainer gridTemplateColumns={gridTemplateColumns}>
        {loadInvoices.status === "PROCESSING" && (
          <>
            <PaymentLoader numItems={ITEMS_PER_PAGE} />
          </>
        )}
        {loadInvoices.status === "SUCCESS" && (
          <>
            {loadInvoices.data.details.length === 0 && (
              <StandaloneMessage>
                No hay ningún volante facturado.
              </StandaloneMessage>
            )}

            {loadInvoices.data.details.length > 0 && (
              <>
                <TableHeader className="item-container">
                  <div className="main-info-header">{titles.importer}</div>

                  <div>{titles.retirement}</div>
                  <div className="main-info-header">{titles.rucId}</div>
                  <div>{titles.status}</div>
                  <div>{"NRO PEDIDO"}</div>
                  <div>{titles.electronicFacNum}</div>
                  <div>{titles.amount}</div>
                  <div>{titles.exportPdf}</div>
                </TableHeader>
                {loadInvoices.data.details.map((invoice) => {
                  const {
                    consigneeRuc,
                    importer,
                    volanteId,
                    withdrawalDate,
                    ruc,
                    amount,
                    electronicFacNum,
                    quotationDoc,
                  } = invoice;
                  const today = moment(
                    new Date(new Date().setHours(0, 0, 0, 0))
                  );
                  return (
                    <div key={invoice.id}>
                      <InvoiceContainer className="item-container">
                        <div className="attribute main-info-header">
                          <div className="titles">{titles.importer}</div>
                          <div className="main-info-header">
                            <p className="id">{consigneeRuc}</p>
                            <p>{importer}</p>
                            <VolanteId>{volanteId}</VolanteId>
                          </div>
                        </div>

                        <div className="attribute">
                          <div className="titles">{titles.retirement}</div>
                          <ColoredTag
                            hasExpired={moment(withdrawalDate) < today}
                          >
                            {dateToRegularFormat(withdrawalDate)}
                          </ColoredTag>
                        </div>

                        <div className="attribute main-info-header">
                          <div className="titles">{titles.rucId}</div>
                          <div className="main-info-header">
                            <p className="ruc-id">{ruc.ruc}</p>
                            <p>{ruc.name}</p>
                          </div>
                        </div>
                        <div className="attribute">
                          <div className="titles">{titles.status}</div>
                          <p style={{ textAlign: "center" }}>
                            {invoice.dam_num_all_reduce_6}
                            <strong>{invoice.dam_num_last_digit_6}</strong>
                          </p>
                        </div>
                        <div className="attribute">
                          <div className="titles">{"NRO PEDIDO"}</div>
                          <div style={{ textAlign: "center" }}>
                            {invoice.factura_num ? invoice.factura_num : "-"}
                          </div>
                        </div>

                        <div
                          className="attribute"
                          style={{ textAlign: "center" }}
                        >
                          <div className="titles">
                            {titles.electronicFacNum}
                          </div>
                          <div>{electronicFacNum ? electronicFacNum : "-"}</div>
                        </div>

                        <div className="attribute">
                          <div className="titles">{titles.amount}</div>
                          <div>{formatMoney(amount, useDollars)}</div>
                        </div>

                        <Authorization actions={["files.download_files"]}>
                          <div className="attribute">
                            <div className="titles">{titles.exportPdf}</div>
                            <ExportButton
                              disabled={!quotationDoc}
                              tooltipText={
                                quotationDoc
                                  ? "Descargar PDF"
                                  : "PDF no disponible"
                              }
                              onClick={() => {
                                handleDownload(quotationDoc);
                              }}
                            />
                          </div>
                        </Authorization>
                      </InvoiceContainer>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </InvoicesContainer>

      {loadInvoices.status === "SUCCESS" && (
        <>
          {loadInvoices.data.count > 0 && (
            <Container>
              <Authorization
                actions={["quotation.get_quotation_paid_backoffice"]}
              >
                <div className="flex-paginator">
                  <Paginator
                    first={
                      (loadInvoices.params.page - 1) *
                      loadInvoices.data.page_size
                    }
                    rows={loadInvoices.data.page_size}
                    totalRecords={loadInvoices.data.count}
                    onPageChange={(e) => {
                      const targetPage = e.first / loadInvoices.data.page_size;
                      const pathname = history.location.pathname;
                      const parsedParams = parse(history.location.search);
                      const stringifiedParams = stringify({
                        ...parsedParams,
                        page: targetPage + 1,
                      });
                      history.push(`${pathname}?${stringifiedParams}`);
                    }}
                  />
                </div>
              </Authorization>
            </Container>
          )}
        </>
      )}
    </>
  );
};

const generateDownloadUrl = (id: string) => {
  return networkBackendRequest(
    "/files/presigned-download-url/",
    HTTP_METHODS.GET,
    {
      object_name: id,
    }
  )
    .then((response) => response.json())
    .then((response) => response.url);
};
